import { Injectable } from '@angular/core';
import { FilterInfo } from '../core-data-model/FilterInfo.model';
import { UserUpdate, User } from '../core-data-model/User.model';
import { ConfirmationHelpers } from '../modals/Confirmation.decorator';
import { notification } from './Notifications.service';
import { UserApi, UserSearchOption } from './UserApi.connect';

@Injectable({
    providedIn: 'root'
})
export class UserDataService {
    constructor(private userApi: UserApi) {}

    updateUserLastLogin() {
        return this.userApi.updateUserLastLogin();
    }

    @notification('notification.usercreate-success')
    createUser(user: UserUpdate) {
        return this.userApi.createUpdateUser(user);
    }

    createUpdateUser(user: UserUpdate) {
        return user.originalUser
            ? this.updateUser(user)
            : this.createNewUser(user);
    }

    @notification('notification.usercreate-success')
    private createNewUser(user: UserUpdate) {
        return this.userApi.createUpdateUser(user);
    }

    @notification('notification.userUpdated-success')
    private updateUser(user: UserUpdate) {
        return this.userApi.createUpdateUser(user);
    }

    getUser(userId: string) {
        return this.userApi.getUser(userId);
    }
    getUsers(
        count: number,
        filters: FilterInfo[],
        isThirdParty: UserSearchOption = 'users'
    ) {
        return this.userApi.getUsers(count, filters, isThirdParty);
    }

    getApiKey(userId: string) {
        return this.userApi.getApiKey(userId);
    }

    updateApiKey(userId: string) {
        return this.userApi.updateApiKey(userId);
    }
    validateEmail(email: string, user: User) {
        return user
            ? this.userApi.validateEmail(email, user.id)
            : this.userApi.validateEmail(email);
    }

    updateUserAccess(
        userId: string,
        isThirdParty: boolean,
        isEnabled: boolean
    ) {
        if (isThirdParty) {
            return this.updateUserAccessWithNotification(
                userId,
                isThirdParty,
                isEnabled
            );
        }
        return isEnabled
            ? this.enableUserAccessWithNotification(userId, isThirdParty)
            : this.disableUserAccessWithConfirmation(userId, isThirdParty);
    }

    @notification('notification.userAccessUpdated-success')
    private enableUserAccessWithNotification(
        userId: string,
        isThirdParty: boolean
    ) {
        return this.userApi.updateUserAccess(userId, isThirdParty, true);
    }

    @notification('notification.userAccessUpdated-success')
    private updateUserAccessWithNotification(
        userId: string,
        isThirdParty: boolean,
        isEnabled: boolean
    ) {
        return this.userApi.updateUserAccess(userId, isThirdParty, isEnabled);
    }

    @ConfirmationHelpers.requireConfirmationDecorator(
        'usermanagement.disable-access.confirmation-title',
        'usermanagement.disable-access.confirmation-subTitle'
    )
    @notification('notification.userAccessUpdated-success')
    private disableUserAccessWithConfirmation(
        userId: string,
        isThirdParty: boolean
    ) {
        return this.userApi.updateUserAccess(userId, isThirdParty, false);
    }

    validateUserByEmail(email: string, shareLinkId: string) {
        return this.userApi.validateUserByEmail(email, shareLinkId);
    }

    async createUserFromShareLink(
        email: string,
        firstName: string,
        lastName: string,
        jobTitle: string,
        shareLinkId: string
    ) {
        return this.userApi.createUserFromShareLink(
            email,
            firstName,
            lastName,
            jobTitle,
            shareLinkId
        );
    }
}
